import React, { useEffect, useState } from "react";
import { API } from "../utils/api";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Users,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Clock,
  DollarSign,
} from "lucide-react";
import { useNavigate } from 'react-router-dom'

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [recentTx, setRecentTx] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("");
  const navigate = useNavigate();

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, txRes] = await Promise.all([
        API.get("/admin/stats"),
        API.get("/admin/transactions"),
      ]);
      setStats(statsRes.data);
      setRecentTx(txRes.data);
      setChartData(statsRes.data.chartData || []);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const pieData = [
    { name: "Active", value: stats.activeClients || 0, color: "#22c55e" },
    {
      name: "Inactive",
      value: (stats.totalclient || 0) - (stats.activeClients || 0),
      color: "#ef4444",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col ">
      {/* Fixed Dark Header with Logo */}
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-6 py-8 flex justify-between items-center">

          {/* Left Section */}
          <div>
            <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Real-time platform overview
            </p>
          </div>

          {/* Gray Button */}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-3 
                 bg-gray-200 text-gray-800 
                 rounded-xl hover:bg-gray-300 
                 transition-all shadow-md disabled:opacity-70"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>

        </div>
      </div>


      {/* Main Content Area */}
      <div className="flex-1 p-6 md:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              {
                label: "Total Balance",
                value: stats?.totalBalance,
                icon: Wallet,
                color: "from-gray-600 to-gray-800",
              },
              {
                label: "Active Users",
                value: stats.activeClients,
                icon: Users,
                color: "from-green-600 to-emerald-700",
                path: "/admin/clients"
              },
              {
                label: "Total Deposits",
                value: stats.totalDeposits,
                icon: ArrowUpRight,
                color: "from-blue-600 to-cyan-700",
              },
              {
                label: "Total Withdrawals",
                value: stats.totalWithdraws,
                icon: ArrowDownRight,
                color: "from-red-600 to-rose-700",
              },
              {
                label: "Pending Requests",
                value: recentTx.filter((tx) => tx.status === "pending").length,
                icon: Clock,
                color: "from-yellow-500 to-amber-600",
                path: '/admin/transections'
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl shadow-xl p-6 border border-gray-200 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={() => navigate(item.path)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-3 rounded-2xl bg-gradient-to-br ${item.color} text-white shadow-lg`}>
                    <item.icon className="w-7 h-7" />
                  </div>

                  {item.label !== "Pending Requests" && (
                    <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" /> +12%
                    </span>
                  )}
                </div>

                <p className="text-gray-600 text-sm">{item.label}</p>
                <p className="text-3xl font-bold text-gray-800 mt-2 flex items-center gap-1">
                  {item.label.includes("Balance") || item.label.includes("Deposits") || item.label.includes("Withdrawals") ? (
                    <>
                      <DollarSign className="w-7 h-7" />
                      {(item.value || 0).toLocaleString()}
                    </>
                  ) : (
                    (item.value || 0).toLocaleString()
                  )}
                </p>
              </div>

            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Line Chart */}
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Weekly Activity</h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f3f4f6" />
                  <XAxis dataKey="date" tick={{ fill: "#6b7280" }} />
                  <YAxis tick={{ fill: "#6b7280" }} />
                  <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px" }} />
                  <Legend />
                  <Line type="monotone" dataKey="deposits" name="Deposits" stroke="#22c55e" strokeWidth={3} dot={{ fill: "#22c55e" }} />
                  <Line type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#ef4444" strokeWidth={3} dot={{ fill: "#ef4444" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">User Status</h3>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Recent Transactions</h3>
              <span className="text-sm text-gray-500 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Last updated: {lastUpdated || "Just now"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="bg-gray-50">
                  <tr className="text-left text-sm text-gray-600">
                    <th className="py-4 px-6">User</th>
                    <th className="py-4 px-6">Type</th>
                    <th className="py-4 px-6">Amount</th>
                    <th className="py-4 px-6">Status</th>
                    <th className="py-4 px-6">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTx.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-12 text-gray-500">
                        No recent transactions
                      </td>
                    </tr>
                  ) : (
                    recentTx.slice(0, 6).map((tx, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50 transition">
                        <td className="py-5 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-800 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {tx.userId?.name?.charAt(0) || "U"}
                            </div>
                            <span className="font-medium text-gray-800">
                              {tx.userId?.name || "Unknown User"}
                            </span>
                          </div>
                        </td>
                        <td className="py-5 px-6">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${["deposit", "roi", "commission"].includes(tx.type)
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                              }`}
                          >
                            {tx.type}
                          </span>
                        </td>

                        <td className="py-5 px-6">
                          {tx.status === "pending" ? (
                            <div className="font-bold text-yellow-600 flex items-center gap-1">
                              <DollarSign className="w-5 h-5" />
                              {tx.amount.toLocaleString()}
                            </div>
                          ) : tx.status === "rejected" ? (
                            <div className="font-bold text-red-600 flex items-center gap-1">
                              <DollarSign className="w-5 h-5" />
                              {tx.amount.toLocaleString()}
                            </div>
                          ) : (
                            <div
                              className={`font-bold flex items-center gap-1 ${["deposit", "roi", "commission"].includes(tx.type)
                                ? "text-green-600"
                                : "text-red-600"
                                }`}
                            >
                              {/* SIGN */}
                              {["deposit", "roi", "commission"].includes(tx.type) ? "+" : "-"}

                              <DollarSign className="w-5 h-5" />

                              {/* SHOW ORIGINAL AMOUNT FOR WITHDRAW */}
                              {tx.type === "withdraw"
                                ? Number(tx.originalAmount || 0).toLocaleString()
                                : Number(tx.amount || 0).toLocaleString()}
                            </div>


                          )}
                        </td>
                        <td className="py-5 px-6 text-sm text-gray-600">
                          {tx.status}
                        </td>
                        <td className="py-5 px-6 text-sm text-gray-600">
                          {new Date(tx.createdAt).toLocaleString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}